import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

import {
  CONTRACT_TEMPLATES,
  prefillUGCTemplate,
} from "../../../constants/contractTemplates";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Check,
  Edit3,
  FileText,
  FileUp,
  Globe,
  Keyboard,
  RotateCcw,
} from "lucide-react";
import { Doc, Id } from "@cvx/_generated/dataModel";
import { Document, Page } from "react-pdf";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { DocumentViewer } from "./DocumentViewer";
import { Label } from "@/components/ui/label";
import { SignatureCanvas } from "./SignatureCanvas";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@cvx/_generated/api";
import { jsPDF } from "jspdf";
import { pdfjs } from "react-pdf";
import { useAccount } from "@/state/useAccount";
import { useConvexMutation } from "@convex-dev/react-query";
import { useMutation } from "@tanstack/react-query";
import { useQuery } from "convex/react";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

// Set up PDF.js worker
if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
}

type SignatureField = {
  fieldId: string;
  signerType: "company" | "creator";
  fieldType: "signature" | "date" | "text";
  label: string;
  position: {
    x: number; // Percentage (0-1) relative to page width
    y: number; // Percentage (0-1) relative to page height
    pageNumber: number; // Which page this field is on (1-indexed)
  };
  size: {
    width: number; // Percentage (0-1) relative to page width
    height: number; // Percentage (0-1) relative to page height
  };
  required: boolean;
  placeholder?: string;
};

export default function ContractTab({
  contract,
}: {
  contract: Doc<"creatorContracts">;
}) {
  // Check if formal agreement already exists using Convex React hook
  const existingAgreement = useQuery(
    api.contractFormalAgreements.getFormalContractAgreement,
    {
      contractId: contract._id,
    },
  );

  // Get current user context
  const { creatorId, appId } = useAccount();
  const isCreator = !!creatorId;
  const isFounder = !!appId;

  // Get additional data for template prefilling
  const app = useQuery(api.apps.getApp, { appId: contract.appId });
  const creator = useQuery(api.creators.getCreatorByCreatorId, {
    creatorId: contract.creatorId,
  });
  const campaign = useQuery(api.campaigns.getCampaign, {
    campaignId: contract.campaignId,
  });
  const currentUser = useQuery(api.app.getCurrentUser, {});

  // Get document URL for existing agreement
  const documentUrl = useQuery(
    api.contractFormalAgreements.getDocumentUrl,
    existingAgreement?.document
      ? { storageId: existingAgreement.document }
      : "skip",
  );

  const { mutateAsync: signDocument } = useMutation({
    mutationFn: useConvexMutation(api.contractFormalAgreements.signDocument),
  });

  const [step, setStep] = useState<
    "choice" | "country" | "template" | "upload" | "annotate"
  >("choice");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [templateContent, setTemplateContent] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedStorageId, setUploadedStorageId] =
    useState<Id<"_storage"> | null>(null);

  // Get document URL for uploaded files
  const uploadDocumentUrl = useQuery(
    api.contractFormalAgreements.getDocumentUrl,
    uploadedStorageId ? { storageId: uploadedStorageId } : "skip",
  );
  const [fileUrl, setFileUrl] = useState<string>("");
  const [templatePdfUrl, setTemplatePdfUrl] = useState<string>(""); // Add state for template PDF URL
  const [signatureFields, setSignatureFields] = useState<SignatureField[]>([]);
  const [selectedFieldType, setSelectedFieldType] = useState<
    "signature" | "date" | "text"
  >("signature");
  const [selectedSignerType, setSelectedSignerType] = useState<
    "company" | "creator"
  >("company");
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingContract, setIsCreatingContract] = useState(false);

  // Signing states
  const [signatureValues, setSignatureValues] = useState<{
    [fieldId: string]: string;
  }>({});
  const [isSigning, setIsSigning] = useState(false);
  const [contractNumPages, setContractNumPages] = useState<number>(0);
  const [contractLoading, setContractLoading] = useState<boolean>(true);
  const [signatureModes, setSignatureModes] = useState<{
    [fieldId: string]: "type" | "draw";
  }>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);
  const { mutateAsync: createFormalAgreement } = useMutation({
    mutationFn: useConvexMutation(
      api.contractFormalAgreements.createFormalAgreement,
    ),
  });

  // Helper function to convert text content to PDF with formatting support
  const convertTextToPDF = (text: string): Blob => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    let y = 20; // Start 20mm from top
    const lineHeight = 6; // 6mm line height
    const pageHeight = 297; // A4 page height in mm
    const bottomMargin = 20; // Bottom margin
    const leftMargin = 20; // Left margin
    const rightMargin = 20; // Right margin
    const pageWidth = 210; // A4 page width in mm
    const textWidth = pageWidth - leftMargin - rightMargin; // Available text width

    // Split text into paragraphs
    const paragraphs = text.split("\n");

    for (const paragraph of paragraphs) {
      if (paragraph.trim() === "") {
        // Empty line - add some spacing
        y += lineHeight * 0.5;
        continue;
      }

      // Parse the paragraph for formatting
      const parts = parseFormattedText(paragraph);
      let currentX = leftMargin;

      for (const part of parts) {
        const { text: partText, isBold } = part;

        if (!partText.trim()) continue;

        // Set font style
        doc.setFont("helvetica", isBold ? "bold" : "normal");
        doc.setFontSize(12);

        // Split text to fit within the line width
        const availableWidth = textWidth - (currentX - leftMargin);
        const lines = doc.splitTextToSize(partText, availableWidth);

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];

          // Check if we need a new page
          if (y + lineHeight > pageHeight - bottomMargin) {
            doc.addPage();
            y = 20;
            currentX = leftMargin;
          }

          // If this is not the first line of the part, start from left margin
          if (i > 0) {
            currentX = leftMargin;
          }

          doc.text(line, currentX, y);

          // Calculate the width of the current line to position next part
          const lineWidth = doc.getTextWidth(line);

          // If this is the last line of this part, move X position
          if (i === lines.length - 1) {
            currentX += lineWidth;
          }

          // If this is not the last line, move to next line
          if (i < lines.length - 1) {
            y += lineHeight;
          }
        }
      }

      // Move to next line after the paragraph
      y += lineHeight;
    }

    return doc.output("blob");
  };

  // Helper function to parse formatted text with **bold** markers
  const parseFormattedText = (
    text: string,
  ): Array<{ text: string; isBold: boolean }> => {
    const parts: Array<{ text: string; isBold: boolean }> = [];
    const regex = /(\*\*.*?\*\*)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the bold part
      if (match.index > lastIndex) {
        const beforeText = text.substring(lastIndex, match.index);
        if (beforeText) {
          parts.push({ text: beforeText, isBold: false });
        }
      }

      // Add the bold text (removing the ** markers)
      const boldText = match[0].replace(/^\*\*|\*\*$/g, "");
      if (boldText) {
        parts.push({ text: boldText, isBold: true });
      }

      lastIndex = regex.lastIndex;
    }

    // Add remaining text after the last bold part
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      if (remainingText) {
        parts.push({ text: remainingText, isBold: false });
      }
    }

    // If no bold parts were found, return the entire text as non-bold
    if (parts.length === 0) {
      parts.push({ text, isBold: false });
    }

    return parts;
  };

  // Convert template to PDF and create preview URL
  const convertTemplateToPdfPreview = (text: string) => {
    const pdfBlob = convertTextToPDF(text);
    const pdfUrl = URL.createObjectURL(pdfBlob);
    setTemplatePdfUrl(pdfUrl);
    return pdfBlob;
  };

  // Initialize signature modes for signature fields - moved outside conditional block
  useEffect(() => {
    if (!existingAgreement) return;

    const fieldsToSign = existingAgreement.signatureFields.filter((field) => {
      const progress = existingAgreement.signatureProgress?.find(
        (p) => p.fieldId === field.fieldId,
      );
      const userType = isCreator ? "creator" : "company";
      return field.signerType === userType && progress?.status === "pending";
    });

    const newModes: { [fieldId: string]: "type" | "draw" } = {};
    fieldsToSign.forEach((field) => {
      if (field.fieldType === "signature" && !signatureModes[field.fieldId]) {
        newModes[field.fieldId] = "type"; // Default to typing mode
      }
    });
    if (Object.keys(newModes).length > 0) {
      setSignatureModes((prev) => ({ ...prev, ...newModes }));
    }
  }, [existingAgreement, isCreator, signatureModes]);

  // Cleanup PDF blob URL on unmount
  useEffect(() => {
    return () => {
      if (templatePdfUrl) {
        URL.revokeObjectURL(templatePdfUrl);
      }
    };
  }, [templatePdfUrl]);

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      try {
        const uploadResponse = uploaded[0].response as {
          storageId: Id<"_storage">;
        };
        setUploadedStorageId(uploadResponse.storageId);

        toast({
          title: "File uploaded successfully",
          description: "You can now add signature fields to the document",
        });

        setStep("annotate");
      } catch (error) {
        console.error("Error processing upload:", error);
        toast({
          title: "Error processing upload",
          description: "There was an error processing the uploaded file",
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
      }
    },
    onUploadError: () => {
      setIsUploading(false);
      toast({
        title: "Error uploading file",
        description: "There was an error uploading the file",
        variant: "destructive",
      });
    },
  });

  const handleTemplateSelect = () => {
    if (!selectedCountry) return;

    const template =
      CONTRACT_TEMPLATES[selectedCountry as keyof typeof CONTRACT_TEMPLATES];
    if (template) {
      let content = template.content;

      // If it's the UGC template and we have the required data, prefill it
      if (selectedCountry === "UK" && app && creator && currentUser) {
        try {
          content = prefillUGCTemplate({
            campaign,
            contract,
            app,
            creator: {
              name: creator.name,
              email: creator.email,
            },
            founder: {
              name: currentUser.name,
            },
          });
        } catch (error) {
          console.error("Error prefilling template:", error);
          // Fall back to the original template if prefilling fails
          content = template.content;
        }
      }

      setTemplateContent(content);
      setStep("template"); // Go to template editing step first
    }
  };

  const handleFileUpload = (file: File) => {
    setUploadedFile(file);
    setIsUploading(true);
    startUpload([file]);
  };

  const handleCreateContract = async () => {
    if (signatureFields.length === 0) {
      toast({
        title: "Signature fields required",
        description:
          "Please add at least one signature field before creating the contract",
        variant: "destructive",
      });
      return;
    }

    setIsCreatingContract(true);

    try {
      let documentStorageId: Id<"_storage">;

      if (templatePdfUrl) {
        // Template was already converted to PDF, fetch the blob and upload it
        const response = await fetch(templatePdfUrl);
        const blob = await response.blob();
        const file = new File([blob], "contract.pdf", {
          type: "application/pdf",
        });

        const uploadUrl = await generateUploadUrl();
        const result = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": file.type },
          body: file,
        });

        const uploadResponse = await result.json();
        documentStorageId = uploadResponse.storageId;
      } else if (uploadedStorageId) {
        documentStorageId = uploadedStorageId;
      } else {
        throw new Error("No document to upload");
      }

      await createFormalAgreement({
        contractId: contract._id,
        document: documentStorageId,
        signatureFields: signatureFields.map((field) => ({
          ...field,
          position: field.position,
          size: field.size,
        })),
      });

      toast({
        title: "Contract created successfully",
        description: "The contract has been uploaded and is ready for signing",
      });

      // Reset to choice step to show success
      setStep("choice");
      setSignatureFields([]);
      setTemplateContent("");
      setUploadedFile(null);
      setUploadedStorageId(null);
      setFileUrl("");
      // Clean up template PDF URL
      if (templatePdfUrl) {
        URL.revokeObjectURL(templatePdfUrl);
        setTemplatePdfUrl("");
      }
    } catch (error) {
      console.error("Error creating formal agreement:", error);
      toast({
        title: "Error creating contract",
        description: "There was an error creating the formal agreement",
        variant: "destructive",
      });
    } finally {
      setIsCreatingContract(false);
    }
  };

  // If still loading, show loading state
  if (existingAgreement === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading contract...</p>
        </div>
      </div>
    );
  }

  // If there's no formal agreement and user is a creator, show waiting message
  if (!existingAgreement && isCreator) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
            <FileText className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-primary">
              Contract Coming Soon
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              The founder is preparing your contract agreement. You'll see it
              here when it's ready for you to review and sign.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If formal agreement exists, show it instead of creation flow
  if (existingAgreement) {
    const currentUserCanSign =
      (existingAgreement.status === "pendingCompanySignature" && isFounder) ||
      (existingAgreement.status === "pendingCreatorSignature" && isCreator);

    const getStatusDisplay = () => {
      switch (existingAgreement.status) {
        case "pendingCompanySignature":
          return {
            text: "Waiting for Company Signature",
            color: "text-orange-600",
            bgColor: "bg-orange-50 dark:bg-orange-900/20",
          };
        case "pendingCreatorSignature":
          return {
            text: "Waiting for Creator Signature",
            color: "text-blue-600",
            bgColor: "bg-blue-50 dark:bg-blue-900/20",
          };
        case "signed":
          return {
            text: "Signed",
            color: "text-green-600",
            bgColor: "bg-green-50 dark:bg-green-900/20",
          };
        default:
          return {
            text: existingAgreement.status,
            color: "text-gray-600",
            bgColor: "bg-gray-50 dark:bg-gray-900/20",
          };
      }
    };

    const statusDisplay = getStatusDisplay();

    // Get fields that need to be signed by current user
    const fieldsToSign = existingAgreement.signatureFields.filter((field) => {
      const progress = existingAgreement.signatureProgress?.find(
        (p) => p.fieldId === field.fieldId,
      );
      const userType = isCreator ? "creator" : "company";
      return field.signerType === userType && progress?.status === "pending";
    });

    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-primary">
            Contract Agreement
          </h2>
          <div
            className={`inline-flex items-center px-3 py-2 rounded-lg ${statusDisplay.bgColor}`}
          >
            <span className={`font-medium ${statusDisplay.color}`}>
              {statusDisplay.text}
            </span>
          </div>
        </div>

        {/* Contract Info */}
        <Card>
          <CardHeader>
            <CardTitle>Contract Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Status
                </p>
                <p className="capitalize">
                  {existingAgreement.status.replace(/([A-Z])/g, " $1").trim()}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Signature Fields
                </p>
                <p>
                  {existingAgreement.signatureFields.length} fields configured
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Completed Signatures
                </p>
                <p>
                  {existingAgreement.signatureProgress?.filter(
                    (p) => p.status === "completed",
                  ).length || 0}{" "}
                  / {existingAgreement.signatureFields.length}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Next Action
                </p>
                <p className="capitalize">
                  {existingAgreement.currentSignerType
                    ? `Waiting for ${existingAgreement.currentSignerType}`
                    : "Complete"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Document Viewer with Signing Interface */}
        {documentUrl && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Contract Document</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (documentUrl) {
                      window.open(documentUrl, "_blank");
                    }
                  }}
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  Download
                </Button>
              </div>
              {currentUserCanSign && fieldsToSign.length > 0 && (
                <CardDescription>
                  You have {fieldsToSign.length} field
                  {fieldsToSign.length !== 1 ? "s" : ""} to sign. Fill out the
                  required fields below and click sign.
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {/* Signature Fields Form */}
              {currentUserCanSign && fieldsToSign.length > 0 && (
                <div className="mb-6 p-4 border rounded-lg bg-muted/30">
                  <h4 className="font-medium mb-4">Fields to Complete:</h4>
                  <div className="space-y-4">
                    {fieldsToSign.map((field) => (
                      <div key={field.fieldId} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor={field.fieldId}>{field.label}</Label>
                          {field.fieldType === "signature" && (
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant={
                                  signatureModes[field.fieldId] === "type"
                                    ? "default"
                                    : "outline"
                                }
                                size="sm"
                                onClick={() => {
                                  setSignatureModes((prev) => ({
                                    ...prev,
                                    [field.fieldId]: "type",
                                  }));
                                  // Clear the signature if switching modes
                                  if (
                                    signatureModes[field.fieldId] === "draw"
                                  ) {
                                    setSignatureValues((prev) => ({
                                      ...prev,
                                      [field.fieldId]: "",
                                    }));
                                  }
                                }}
                              >
                                <Keyboard className="h-3 w-3 mr-1" />
                                Type
                              </Button>
                              <Button
                                type="button"
                                variant={
                                  signatureModes[field.fieldId] === "draw"
                                    ? "default"
                                    : "outline"
                                }
                                size="sm"
                                onClick={() => {
                                  setSignatureModes((prev) => ({
                                    ...prev,
                                    [field.fieldId]: "draw",
                                  }));
                                  // Clear the signature if switching modes
                                  if (
                                    signatureModes[field.fieldId] === "type"
                                  ) {
                                    setSignatureValues((prev) => ({
                                      ...prev,
                                      [field.fieldId]: "",
                                    }));
                                  }
                                }}
                              >
                                <Edit3 className="h-3 w-3 mr-1" />
                                Draw
                              </Button>
                            </div>
                          )}
                        </div>
                        {field.fieldType === "signature" ? (
                          signatureModes[field.fieldId] === "draw" ? (
                            <SignatureCanvas
                              onSignatureChange={(signature) => {
                                setSignatureValues((prev) => ({
                                  ...prev,
                                  [field.fieldId]: signature,
                                }));
                              }}
                              width={400}
                              height={120}
                            />
                          ) : (
                            <div className="relative">
                              <input
                                id={field.fieldId}
                                type="text"
                                placeholder="Type your full name to sign"
                                value={signatureValues[field.fieldId] || ""}
                                onChange={(e) =>
                                  setSignatureValues((prev) => ({
                                    ...prev,
                                    [field.fieldId]: e.target.value,
                                  }))
                                }
                                className="w-full px-3 py-2 border rounded-md font-cursive text-lg"
                                style={{ fontFamily: "cursive" }}
                              />
                            </div>
                          )
                        ) : field.fieldType === "date" ? (
                          <input
                            id={field.fieldId}
                            type="date"
                            value={signatureValues[field.fieldId] || ""}
                            max={new Date().toISOString().split("T")[0]}
                            onChange={(e) =>
                              setSignatureValues((prev) => ({
                                ...prev,
                                [field.fieldId]: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded-md"
                          />
                        ) : (
                          <input
                            id={field.fieldId}
                            type="text"
                            placeholder={
                              field.placeholder || `Enter ${field.fieldType}`
                            }
                            value={signatureValues[field.fieldId] || ""}
                            onChange={(e) =>
                              setSignatureValues((prev) => ({
                                ...prev,
                                [field.fieldId]: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded-md"
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex gap-3">
                    <Button
                      onClick={async () => {
                        setIsSigning(true);
                        try {
                          // Prepare signatures array from current values
                          const signatures = fieldsToSign.map((field) => ({
                            fieldId: field.fieldId,
                            value: signatureValues[field.fieldId] || "",
                          }));

                          // Call the signing mutation
                          const result = await signDocument({
                            contractId: contract._id,
                            signatures,
                          });

                          // Show success message with status info
                          toast({
                            title: "Document signed successfully",
                            description: `Your signature has been recorded. ${result.completedSignatures}/${result.totalSignatures} signatures complete.`,
                          });

                          // Clear signature values after successful signing
                          setSignatureValues({});
                          setSignatureModes({});
                        } catch (error) {
                          console.error("Error signing document:", error);
                          const errorMessage =
                            error instanceof Error
                              ? error.message
                              : "There was an error recording your signature.";

                          toast({
                            title: "Error signing document",
                            description: errorMessage,
                            variant: "destructive",
                          });
                        } finally {
                          setIsSigning(false);
                        }
                      }}
                      disabled={
                        isSigning ||
                        fieldsToSign.some(
                          (field) => !signatureValues[field.fieldId],
                        )
                      }
                    >
                      {isSigning ? "Signing..." : "Sign Document"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Document Display */}
              <div className="relative bg-white dark:bg-gray-900 border rounded-lg p-4 min-h-[600px] overflow-auto">
                {contractLoading && (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center space-y-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                      <p className="text-muted-foreground">Loading PDF...</p>
                    </div>
                  </div>
                )}

                <div className="space-y-4 max-w-4xl mx-auto">
                  <Document
                    file={documentUrl}
                    onLoadSuccess={({ numPages }) => {
                      setContractNumPages(numPages);
                      setContractLoading(false);
                    }}
                    onLoadError={(error) => {
                      console.error("Error loading PDF:", error);
                      setContractLoading(false);
                    }}
                    className="w-full"
                  >
                    {Array.from(new Array(contractNumPages), (_, index) => {
                      const pageNumber = index + 1;
                      return (
                        <div
                          key={`contract_page_${pageNumber}`}
                          className="relative mb-4 border border-gray-200 dark:border-gray-700 rounded bg-white mx-auto"
                          style={{ width: "fit-content", maxWidth: "100%" }}
                        >
                          <Page
                            pageNumber={pageNumber}
                            width={800}
                            className="w-full"
                            renderAnnotationLayer={false}
                            renderTextLayer={false}
                          />

                          {/* Page number indicator */}
                          <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                            Page {pageNumber}
                          </div>

                          {/* Show signature field overlays (read-only) - only if not fully signed */}
                          {existingAgreement.status !== "signed" &&
                            existingAgreement.signatureFields
                              .filter(
                                (field) =>
                                  field.position.pageNumber === pageNumber,
                              )
                              .map((field) => {
                                const progress =
                                  existingAgreement.signatureProgress?.find(
                                    (p) => p.fieldId === field.fieldId,
                                  );
                                const isCompleted =
                                  progress?.status === "completed";

                                return (
                                  <div
                                    key={field.fieldId}
                                    className={`absolute border-2 pointer-events-none ${
                                      isCompleted
                                        ? "border-green-300 bg-green-100/80"
                                        : field.signerType === "company"
                                          ? "border-blue-300 bg-blue-100/80"
                                          : "border-purple-300 bg-purple-100/80"
                                    }`}
                                    style={{
                                      left: `${field.position.x * 100}%`,
                                      top: `${field.position.y * 100}%`,
                                      width: `${field.size.width * 100}%`,
                                      height: `${field.size.height * 100}%`,
                                      zIndex: 10,
                                    }}
                                  >
                                    <div className="flex items-center justify-center h-full px-2 text-xs font-medium">
                                      {isCompleted ? (
                                        <span className="text-green-700">
                                          ✓ {field.label}
                                        </span>
                                      ) : (
                                        <span
                                          className={
                                            field.signerType === "company"
                                              ? "text-blue-700"
                                              : "text-purple-700"
                                          }
                                        >
                                          {field.label}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                        </div>
                      );
                    })}
                  </Document>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Signature Progress */}
        {existingAgreement.signatureProgress &&
          existingAgreement.signatureProgress.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Signature Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {existingAgreement.signatureProgress.map(
                    (progress, index) => (
                      <div
                        key={progress.fieldId}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              progress.status === "completed"
                                ? "bg-green-100 text-green-600"
                                : "bg-gray-100 text-gray-400"
                            }`}
                          >
                            {progress.status === "completed" ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <span className="text-xs font-medium">
                                {index + 1}
                              </span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">
                              {
                                existingAgreement.signatureFields.find(
                                  (f) => f.fieldId === progress.fieldId,
                                )?.label
                              }
                            </p>
                            <p className="text-sm text-muted-foreground capitalize">
                              {progress.signerType} {progress.status}
                            </p>
                            {/* Show the actual signature value when completed */}
                            {progress.status === "completed" &&
                              progress.value && (
                                <div className="mt-2">
                                  {(() => {
                                    const field =
                                      existingAgreement.signatureFields.find(
                                        (f) => f.fieldId === progress.fieldId,
                                      );

                                    // Check if it's a base64 image (signature)
                                    if (
                                      field?.fieldType === "signature" &&
                                      progress.value &&
                                      progress.value.startsWith("data:image/")
                                    ) {
                                      // Display signature as image
                                      return (
                                        <div className="bg-white border border-gray-300 rounded p-2 inline-block">
                                          <img
                                            src={progress.value}
                                            alt="Signature"
                                            className="max-w-[200px] max-h-[60px] object-contain"
                                            onError={(e) => {
                                              console.error(
                                                "Failed to load signature image:",
                                                progress.value,
                                              );
                                              // Fallback to text if image fails to load
                                              e.currentTarget.style.display =
                                                "none";
                                              if (e.currentTarget.nextSibling) {
                                                (
                                                  e.currentTarget
                                                    .nextSibling as HTMLElement
                                                ).style.display = "block";
                                              }
                                            }}
                                          />
                                          <div
                                            style={{ display: "none" }}
                                            className="text-xs text-gray-500"
                                          >
                                            Signature image could not be
                                            displayed
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      // Display text values (dates, names, etc.)
                                      return (
                                        <p className="text-sm bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded border max-w-[200px] break-words font-mono">
                                          {progress.value}
                                        </p>
                                      );
                                    }
                                  })()}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span
                            className={`text-xs px-2 py-1 rounded ${
                              progress.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {progress.status}
                          </span>
                          {progress.signedAt && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(progress.signedAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </CardContent>
            </Card>
          )}

        {/* Action Messages */}
        {!currentUserCanSign && existingAgreement.status !== "signed" && (
          <div className="text-center p-4 bg-muted/30 rounded-lg">
            <p className="text-muted-foreground">
              {existingAgreement.currentSignerType === "company"
                ? "Waiting for company to sign the contract"
                : existingAgreement.currentSignerType === "creator"
                  ? "Waiting for creator to sign the contract"
                  : "All signatures collected"}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Initial choice screen
  if (step === "choice") {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-primary">
            Create Formal Contract
          </h2>
          <p className="text-muted-foreground">
            Choose how you'd like to create your contract agreement
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <Card
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setStep("country")}
          >
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <CardTitle>Use a Template</CardTitle>
              <CardDescription>
                Start with a pre-built contract template customized for your
                region
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  Legally reviewed templates
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  Country-specific compliance
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  Easy customization
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setStep("upload")}
          >
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <FileUp className="h-6 w-6 text-primary" />
              </div>
              <CardTitle>Upload Your Own</CardTitle>
              <CardDescription>
                Upload your own contract document (PDF, DOC, DOCX)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  Use your existing contracts
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  Full control over terms
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  Support for PDF files
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Country selection step
  if (step === "country") {
    return (
      <div className="space-y-6 max-w-2xl mx-auto">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-primary">
            Select Your Region
          </h2>
          <p className="text-muted-foreground">
            Choose your company's region to get the appropriate contract
            template
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Company Location
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="country">Country/Region</Label>
              <Select
                value={selectedCountry}
                onValueChange={setSelectedCountry}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your country/region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedCountry && (
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">
                  {
                    CONTRACT_TEMPLATES[
                      selectedCountry as keyof typeof CONTRACT_TEMPLATES
                    ].name
                  }
                </h4>
                <p className="text-sm text-muted-foreground">
                  {
                    CONTRACT_TEMPLATES[
                      selectedCountry as keyof typeof CONTRACT_TEMPLATES
                    ].description
                  }
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep("choice")}>
                Back
              </Button>
              <Button
                onClick={handleTemplateSelect}
                disabled={!selectedCountry}
              >
                Continue with Template
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Template editing step
  if (step === "template") {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-primary">
            Edit Your Contract
          </h2>
          <p className="text-muted-foreground">
            Customize the template to match your specific requirements
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Contract Template</CardTitle>
            <CardDescription>
              Edit the text below to customize your contract.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={templateContent}
              onChange={(e) => setTemplateContent(e.target.value)}
              className="w-full h-96 font-mono text-sm resize-none"
              placeholder="Contract content..."
            />

            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={() => setStep("country")}>
                Back
              </Button>
              <Button
                onClick={() => {
                  // Convert template to PDF before proceeding to annotation
                  convertTemplateToPdfPreview(templateContent);
                  setStep("annotate");
                }}
              >
                Continue to Signature Fields
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // File upload step
  if (step === "upload") {
    return (
      <div className="space-y-6 max-w-2xl mx-auto">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-primary">
            Upload Contract Document
          </h2>
          <p className="text-muted-foreground">
            Upload your contract document (PDF, DOC, DOCX)
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
              {isUploading ? (
                <div className="space-y-4">
                  <div className="mx-auto w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                  <div>
                    <p className="font-medium">Uploading file...</p>
                    <p className="text-sm text-muted-foreground">
                      Please wait while we process your file
                    </p>
                  </div>
                </div>
              ) : uploadedFile ? (
                <div className="space-y-4">
                  <div className="mx-auto w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Check className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{uploadedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setUploadedFile(null);
                      setUploadedStorageId(null);
                      setFileUrl("");
                    }}
                  >
                    Choose Different File
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="mx-auto w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                    <FileUp className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">Upload Contract Document</p>
                    <p className="text-sm text-muted-foreground">
                      PDF, DOC, or DOCX files up to 10MB
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                    className="hidden"
                  />
                  <Button onClick={() => fileInputRef.current?.click()}>
                    Choose File
                  </Button>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setStep("choice")}>
                Back
              </Button>
              <Button
                onClick={() => setStep("annotate")}
                disabled={!uploadedStorageId || isUploading}
              >
                Continue to Signature Fields
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Document annotation step
  if (step === "annotate") {
    return (
      <div className="space-y-6 relative">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-primary">
            Add Signature Fields
          </h2>
          <p className="text-muted-foreground">
            Click anywhere on the document to place signature fields where
            people need to sign
          </p>
        </div>

        {/* Floating Toolbar */}
        <div className="fixed botom-20 right-4 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 max-w-sm">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Field Type:</Label>
              <Select
                value={selectedFieldType}
                onValueChange={(value: "signature" | "date" | "text") =>
                  setSelectedFieldType(value)
                }
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="signature">Signature</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="text">Text</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Signer:</Label>
              <Select
                value={selectedSignerType}
                onValueChange={(value: "company" | "creator") =>
                  setSelectedSignerType(value)
                }
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-200 border border-blue-300 rounded"></div>
                      Company
                    </span>
                  </SelectItem>
                  <SelectItem value="creator">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-purple-200 border border-purple-300 rounded"></div>
                      Creator
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSignatureFields([])}
                disabled={signatureFields.length === 0}
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Clear All
              </Button>
              <span className="text-xs text-muted-foreground">
                {signatureFields.length} field
                {signatureFields.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </div>

        {/* Document Viewer */}
        <DocumentViewer
          fileUrl={templatePdfUrl || uploadDocumentUrl || fileUrl}
          signatureFields={signatureFields}
          onSignatureFieldsChange={setSignatureFields}
          selectedFieldType={selectedFieldType}
          selectedSignerType={selectedSignerType}
        />

        {/* Actions */}
        <div className="flex gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => setStep(templatePdfUrl ? "country" : "upload")}
          >
            Back
          </Button>
          <Button
            onClick={handleCreateContract}
            disabled={!signatureFields.length || isCreatingContract}
          >
            {isCreatingContract ? "Creating Contract..." : "Create Contract"}
          </Button>
        </div>

        {!signatureFields.length && (
          <div className="text-center text-sm text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400 p-3 rounded-lg">
            Add at least one signature field to continue
          </div>
        )}
      </div>
    );
  }

  return <div>ContractTab</div>;
}
